/**
 * Preparo da foto do acompanhante ANTES de subir.
 *
 * A selfie do titular já sobe reduzida — o `captureVideoFrameAsDataUrl` desenha
 * o frame num canvas de 640px e exporta JPEG 0.85 (dezenas de KB). A foto do
 * acompanhante ia CRUA, como saiu da câmera: um arquivo de 7 MB vira ~9,3 MB de
 * base64 e atravessa hosting → BFF → service → função de face. No último hop o
 * `fetch` do service cai e o titular recebe "Serviço de biometria indisponível
 * no momento" — que parece falha da biometria, mas é o tamanho do payload.
 *
 * Aqui a imagem é redesenhada com no máximo 1280px no maior lado (folga de
 * sobra para o rosto: a indexação do titular funciona com 640) e exportada em
 * JPEG 0.85 — tipicamente 150–400 KB. Sem canvas disponível (jsdom, browser
 * antigo) devolve o data URL original: o limite de 7 MB continua valendo e o
 * service segue sendo quem valida o rosto.
 */

const MAX_EDGE = 1280;
const JPEG_QUALITY = 0.85;

/** ~7 MB — o BFF/service aceitam até ~10 MB de body (base64 infla ~33%). */
export const MAX_PHOTO_BYTES = 7 * 1024 * 1024;

export type CompanionPhotoError = 'too_large' | 'invalid_type';

function readAsDataUrl(file: File): Promise<string | null> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : null);
    reader.onerror = () => resolve(null);
    reader.readAsDataURL(file);
  });
}

/** Canvas ausente/sem contexto 2D (jsdom): o caminho vira "sobe como veio". */
function canDownscale(): boolean {
  try {
    const canvas = document.createElement('canvas');
    return typeof canvas.toDataURL === 'function' && canvas.getContext('2d') !== null;
  } catch {
    return false;
  }
}

function decode(dataUrl: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => resolve(null);
    image.src = dataUrl;
  });
}

async function downscale(dataUrl: string): Promise<string | null> {
  if (!canDownscale()) return null;

  const image = await decode(dataUrl);
  if (image === null || image.naturalWidth === 0 || image.naturalHeight === 0) return null;

  const scale = Math.min(1, MAX_EDGE / Math.max(image.naturalWidth, image.naturalHeight));
  // Já pequena o bastante: reencodar só perderia qualidade à toa.
  if (scale === 1) return null;

  const canvas = document.createElement('canvas');
  canvas.width = Math.round(image.naturalWidth * scale);
  canvas.height = Math.round(image.naturalHeight * scale);
  const context = canvas.getContext('2d');
  if (context === null) return null;

  context.drawImage(image, 0, 0, canvas.width, canvas.height);
  try {
    return canvas.toDataURL('image/jpeg', JPEG_QUALITY);
  } catch {
    // Canvas "tainted" ou export não suportado — sobe o original.
    return null;
  }
}

/** Valida tipo/tamanho e devolve o data URL já reduzido (ou o erro do arquivo). */
export async function readCompanionPhoto(file: File): Promise<string | CompanionPhotoError> {
  if (!file.type.startsWith('image/')) return 'invalid_type';
  if (file.size > MAX_PHOTO_BYTES) return 'too_large';

  const original = await readAsDataUrl(file);
  if (original === null) return 'invalid_type';

  return (await downscale(original)) ?? original;
}
